import { useState } from 'react'

import { useTexte } from '../../texte'
import { useTageskarte, useWetterkarte } from '../../hooks/queries'
import { api, fehlertext, netzStand } from '../../api/client'
import type { DayCardPreview, MapStyle } from '../../api/types'
import { holeOrt } from '../../lib/ortung'
import { zeichneTagesroute } from './tagesroute'
import { STRICH_VORGABE } from '../map/strich'
import { useBuchquelle } from '../export/buchquelle'

/**
 * What sits behind the `+`: the weather, the weather somewhere else, the day
 * card, the poll and the GIF picker.
 *
 * Pulled out of `Composer.tsx` in 1.23.0. Five entries, and each one has the
 * same shape: a sheet that opens, a question to the server, a line saying
 * which step is running, and a sentence instead of silence when there is no
 * net. That shape is the reason for one place; a sixth entry would otherwise
 * be the sixth copy.
 *
 * **A hook and not a component**, although the plan said `Plusmenue.tsx`.
 * Measured: `.anhangwahl` and `.gifwahl` are blocks in the flow, siblings of
 * `.composer` inside `.composer-area` -- the `+` itself is a child of
 * `.composer`, and the status line belongs among the other status lines above
 * both. A component holding all three would have to move nodes in the tree;
 * the rule for this rebuild is that code moves and nothing else does. So the
 * code moves and the tree stays.
 */
export function usePlusMenu({
  tripId,
  istEntwurf,
  reiseKarte,
}: {
  tripId: number
  /** Schreibt diese Zeile Entwürfe? Die Karte geht dann in den Entwurf. */
  istEntwurf: boolean
  /** Die Farben der Reise -- die Tageskarte zeichnet ihre eigene Linie damit. */
  reiseKarte: MapStyle | undefined
}) {
  const t = useTexte()
  /** Steht das Blatt mit „Foto oder Video / GIF suchen" offen? */
  const [anhangOffen, setAnhangOffen] = useState(false)
  const [gifOffen, setGifOffen] = useState(false)
  const [abstimmungOffen, setAbstimmungOffen] = useState(false)
  const wetter = useWetterkarte(tripId)
  /** "Wo und wann?" is open (#83). */
  const [wetterwahlOffen, setWetterwahlOffen] = useState(false)
  const tageskarte = useTageskarte(tripId)
  const buchquelle = useBuchquelle(tripId)
  /** The days to choose from, while the question "Welcher Tag?" is open (0.41.0). */
  const [tagwahl, setTagwahl] = useState<DayCardPreview | null>(null)
  const [aktionStand, setAktionStand] = useState<string | null>(null)

  /**
   * Get the position, send the weather -- one tap, two steps, and each says
   * what it does. Without the net not at all: no silent failure.
   */
  async function wetterSchicken() {
    if (!netzStand()) {
      setAktionStand(t.chat.wetterOhneNetz)
      return
    }
    setAktionStand(t.chat.wetterOrtung)
    try {
      const ort = await holeOrt()
      setAktionStand(t.chat.wetterHolen)
      await wetter.mutateAsync({ lat: ort.lat, lon: ort.lon, draft: istEntwurf })
      setAktionStand(null)
    } catch (fehler) {
      setAktionStand(fehlertext(fehler))
    }
  }

  /** The weather at a chosen place and hour (#83): the sheet chose, the server asks Apple. */
  async function wetterAnderswoSchicken(wahl: { lat: number; lon: number; at: string }) {
    setWetterwahlOffen(false)
    if (!netzStand()) {
      setAktionStand(t.chat.wetterOhneNetz)
      return
    }
    setAktionStand(t.chat.wetterHolen)
    try {
      await wetter.mutateAsync({ ...wahl, draft: istEntwurf })
      setAktionStand(null)
    } catch (fehler) {
      setAktionStand(fehlertext(fehler))
    }
  }

  /**
   * The day card: ask the server, draw the map, send -- three steps, each
   * says what it does. Without the net not at all.
   */
  async function tagFragen() {
    if (!netzStand()) {
      setAktionStand(t.chat.tagOhneNetz)
      return
    }
    setAktionStand(t.chat.tagAuswerten)
    try {
      // The days the route has, and the proposal -- the question comes
      // before any drawing, so that nothing is drawn for the wrong day.
      const vorschau = await api.get<DayCardPreview>(`/api/trips/${tripId}/daycard`)
      setAktionStand(null)
      setTagwahl(vorschau)
    } catch (fehler) {
      setAktionStand(fehlertext(fehler))
    }
  }

  async function tagSchicken(wahl: { day: string; from?: string; to?: string }) {
    setTagwahl(null)
    setAktionStand(t.chat.tagAuswerten)
    // The day card's own stroke (#80; until then the map's, #50): the map
    // of the day is a picture in the chat, and a line that reads well on
    // the live map can be too thin there. The pins carry the colour of a
    // published point, as on the map.
    const strich = {
      farbe: reiseKarte?.daycard_route_color ?? STRICH_VORGABE.farbe,
      breite: reiseKarte?.daycard_route_width ?? STRICH_VORGABE.breite,
      nadel: reiseKarte?.pin_color ?? '#007aff',
    }
    try {
      await tageskarte.mutateAsync({
        ...wahl,
        draft: istEntwurf,
        zeichnen: (punkte, nadeln) => zeichneTagesroute(punkte, buchquelle, strich, nadeln),
        vorher: (stand) => setAktionStand(stand === 'zeichnen' ? t.chat.tagZeichnen : t.chat.tagSchicken),
      })
      setAktionStand(null)
    } catch (fehler) {
      setAktionStand(fehlertext(fehler))
    }
  }

  return {
    anhangOffen,
    setAnhangOffen,
    gifOffen,
    setGifOffen,
    abstimmungOffen,
    setAbstimmungOffen,
    wetterwahlOffen,
    setWetterwahlOffen,
    tagwahl,
    setTagwahl,
    /** Welcher Schritt gerade läuft -- eine Zeile über der Eingabezeile. */
    aktionStand,
    wetterSchicken,
    wetterAnderswoSchicken,
    tagFragen,
    tagSchicken,
  }
}
